import React, { useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { EmailDomains } from "../../../assets/data/Constants";
import { ITkEmailInputProps } from "../../../type/componentsType/TkEmailInput";
import TkLabelText from "../../atoms/TkLabelText";

/**
 * TkEmailInputコンポーネント
 * @param {ITkEmailInputProps} props - コンポーネントのプロパティ
 * @returns {React.FC} - TkEmailInputコンポーネント
 */
const TkEmailInput: React.FC<ITkEmailInputProps> = (props) => {
  const {
    onChange,
    label = "メールアドレス",
    helperText,
    wantToSetValue,
    ...rest
  } = props;

  const domains = Object.values(EmailDomains);
  const [mailValue, setMailValue] = React.useState<string | null>(null);
  const [beforeAt, setBeforeAt] = React.useState<string>("");

  useEffect(
    () => {
      if (wantToSetValue) {
        setMailValue(wantToSetValue);
        onChange && onChange({ target: { value: wantToSetValue } } as any);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wantToSetValue]
  );

  /**
   * オプションをフィルタリングする
   * @param {string[]} options - オプションのリスト
   * @param {any} state - コンポーネントの状態
   * @returns {string[]} - フィルタリングされたオプションのリスト
   */
  const filterOptions = (options: string[], state: any) => {
    const atIndex = state.inputValue.indexOf("@");
    if (atIndex !== -1) {
      const afterAt = state.inputValue.substring(atIndex + 1);
      return options.filter((option) => option.includes(afterAt));
    }
    return [];
  };

  /**
   * 入力の変更を処理する
   * @param {React.SyntheticEvent<Element, Event>} event - イベントオブジェクト
   * @param {string} newInputValue - 新しい入力値
   */
  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => {
    const atIndex = newInputValue.indexOf("@");

    if (event?.type === "change") {
      const beforeAt =
        atIndex !== -1 ? newInputValue.substring(0, atIndex) : newInputValue;
      const afterAt: any =
        atIndex !== -1 ? newInputValue.substring(atIndex) : "";

      setMailValue(beforeAt + afterAt);
      setBeforeAt(beforeAt);
      onChange && onChange({ target: { value: beforeAt + afterAt } } as any);
    }
  };

  /**
   * 選択の変更を処理する
   * @param {React.ChangeEvent<{}>} _ - イベントオブジェクト
   * @param {string | null} newValue - 新しい選択値
   */
  const handleChange = (_: React.ChangeEvent<{}>, newValue: string | null) => {
    if (newValue === null) {
      setMailValue("");
      onChange && onChange({ target: { value: "" } } as any);
      return;
    }

    setMailValue(`${beforeAt}${newValue}`);
    onChange &&
      onChange({
        target: { value: `${beforeAt}${newValue}` },
      } as any);
  };

  return (
    <Autocomplete
      {...rest}
      freeSolo
      value={mailValue}
      options={domains.map((option) => `@${option}`)}
      filterOptions={filterOptions}
      onInputChange={handleInputChange}
      onChange={handleChange}
      fullWidth
      renderInput={(params) => (
        <TkLabelText
          {...params}
          error={!!helperText}
          helperText={helperText}
          label={label}
        />
      )}
      data-testid="TkEmailInputData"
    />
  );
};

export default TkEmailInput;
